import { INCLUDES } from '@shared/utils'
import type {
  Configuration,
  ConfigurationSchema,
} from 'src/configuration.types'
import type { ProxyRecord } from 'src/services/deserializers/deserializers.types'
import type { JsonSchemaPropertyByFieldKey } from 'src/services/mappings/jsonSchema/jsonSchema.types2'
import { mapSchema } from 'src/services/mappings/jsonSchema/jsonSchema.utils'
import type { ProductKind } from 'src/services/mappings/portfolio/portfolio.const'
import { SUB_RESOURCES } from 'src/services/request.const'
import type { RequestService } from 'src/services/request.types'
import {
  OBJECT_TYPES,
  type ObjectType,
} from 'src/tpbp-configuration/models/models.const'
import type { PortfolioConfigurationModel } from 'src/tpbp-json-api-handler/models/models'
import { StoreDictionary } from 'src/tpbp-json-api-handler/utils/StoreDictionary/StoreDictionary'

const storeDictionary = new StoreDictionary()

const STORAGE_KEYS = {
  configurations: 'configurations',
  schema: 'schema',
  rules: 'rules',
  additionalRules: 'additionalRules',
  configurationKind: 'configurationKind',
  data: 'data',
}

export function getConfigurations<T>(objectType): T[] {
  return storeDictionary.get(STORAGE_KEYS.configurations, objectType) ?? []
}
function getSchemaStorageKey(objectType) {
  return `${STORAGE_KEYS.schema}-${objectType}`
}

function getRulesStorageKey(objectType) {
  return `${STORAGE_KEYS.rules}-${objectType}`
}

function getAdditionalRulesStorageKey(objectType) {
  return `${STORAGE_KEYS.additionalRules}-${objectType}`
}

function getConfigurationByStorageKey(objectType) {
  return `${STORAGE_KEYS.configurationKind}-${objectType}`
}

export function getConfigurationFieldKey(objectType) {
  if (objectType === OBJECT_TYPES.portfolio) {
    return 'productKind'
  }
  if (objectType === OBJECT_TYPES.person) {
    return 'creationType'
  }
  return ''
}

export function getConfiguration<T = Configuration>(
  objectType: ObjectType,
  productKind: ProductKind,
): T | undefined {
  if (!productKind) return

  const storageKey = getConfigurationByStorageKey(objectType)
  return storeDictionary.get<T>(storageKey, productKind) ?? undefined
}

function getKind(record: ProxyRecord) {
  const objectType = record.$type
  const configurationFieldKey = getConfigurationFieldKey(objectType)
  if (configurationFieldKey === '') {
    throw new Error(`No configurationFieldKey for objectType ${objectType}`)
  }
  return record[configurationFieldKey] as ProductKind
}

export function getSchema(record: ProxyRecord) {
  const objectType = record.$type
  const kind = getKind(record)

  if (!kind) return

  const schemaStorageKey = getSchemaStorageKey(objectType)

  if (storeDictionary.has(schemaStorageKey, kind)) {
    return (
      storeDictionary.get<ConfigurationSchema>(schemaStorageKey, kind) ??
      undefined
    )
  }
  const configuration = getConfiguration(objectType, kind)
  const schema = configuration?.schema

  storeDictionary.set(schemaStorageKey, kind, schema)

  return schema
}

export function getRules(
  record: ProxyRecord,
): JsonSchemaPropertyByFieldKey | undefined | null {
  const objectType = record.$type
  const kind = getKind(record)
  if (!kind) {
    return
  }
  const rulesStorageKey = getRulesStorageKey(objectType)
  if (storeDictionary.has(rulesStorageKey, kind)) {
    // eslint-disable-next-line consistent-return
    return storeDictionary.get(rulesStorageKey, kind)
  }

  const schema = getSchema(record)
  const rules = schema ? mapSchema(schema) : {}

  storeDictionary.set(rulesStorageKey, kind, rules)

  // eslint-disable-next-line consistent-return
  return rules
}

export function getAdditionalRules(record) {
  const objectType = record.$type
  const kind = getKind(record)
  if (!kind) return
  const storageKey = getAdditionalRulesStorageKey(objectType)
  if (storeDictionary.has(storageKey, kind)) {
    // eslint-disable-next-line consistent-return
    return storeDictionary.get(storageKey, kind)
  }

  const configuration = getConfiguration(objectType, kind)
  const additionalRules = configuration?.additionalRules ?? {}

  storeDictionary.set(storageKey, kind, additionalRules)

  // eslint-disable-next-line consistent-return
  return additionalRules
}

export function setConfigurations(objectType, configurations) {
  storeDictionary.set(STORAGE_KEYS.configurations, objectType, configurations)
  const storageKey = getConfigurationByStorageKey(objectType)
  const configurationFieldKey = getConfigurationFieldKey(objectType)
  if (configurationFieldKey === '') {
    throw new Error(`No configurationFieldKey for objectType ${objectType}`)
  }
  for (const configuration of configurations) {
    storeDictionary.set(
      storageKey,
      configuration[configurationFieldKey],
      configuration,
    )
  }
}

export function getStoredData(key, callback?) {
  if (!storeDictionary.has(STORAGE_KEYS.data, key)) {
    if (callback) {
      const data = callback()

      storeDictionary.set(STORAGE_KEYS.data, key, data)

      return data
    }
    throw new Error(`${key} not stored`)
  }

  return storeDictionary.get(STORAGE_KEYS.data, key)
}

export async function storeDataIfNecessary(key, callback) {
  if (storeDictionary.has(STORAGE_KEYS.data, key)) {
    return
  }
  const data = await callback()

  storeDictionary.set(STORAGE_KEYS.data, key, data)
}

export async function initConfigurationIfNecessary(
  objectType: ObjectType,
  requestService: RequestService,
  include?: string[],
) {
  if (storeDictionary.has(STORAGE_KEYS.configurations, objectType)) {
    return
  }

  const query = include ? INCLUDES.custom(include) : ''

  const configurations = await requestService.getRessource<
    PortfolioConfigurationModel[]
  >(objectType, SUB_RESOURCES.configurations, query)

  setConfigurations(objectType, configurations)
}
